import React from 'react';
import PropTypes from 'prop-types';

import ImageUploadConfigModel from '../../models/imageUploadConfig';
import { useTranslation } from 'react-i18next';
import { Form } from '@weezevent/nacre';
import { UploadDropZone } from '../../nacre/uploadButton';
import './uploadFileZone.scss';
import UploadPreview from './components/uploadPreview';
import ModalEditImage from './components/modalEditImage';

function getImageDimensions(file) {
    return new Promise((resolve, reject) => {
        const img = document.createElement('img');
        img.onload = () => {
            resolve(img.naturalWidth * img.naturalHeight);
        };
        img.onerror = err => {
            reject(err);
        };
        img.src = URL.createObjectURL(file);
    });
}

export default function UploadFileZone({
    disabled = false,
    organization,
    onUpload = () => {},
    onError = () => {},
    onDelete = () => {},
    onSelect = () => {},
    customErrors = null,
    file = null,
    getURLParams = () => ({}),
    getQueryParams = () => ({}),
    shouldUpload = () => true,
    fileTypes = UploadFileZone.imageFileTypes,
    labelDropzone = null,
    subLabelDropzone = null,
    title = null,
    subtitle = null,
    maxSize = 5242880,
    maxPixels = 0,
    optional = false,
    className = '',
    uploadConfigModel = ImageUploadConfigModel,
    cropModalProps = {
        ratio: null,
        forceRatio: false,
        minWidth: 180,
        maxWidth: Infinity,
        minHeight: 180,
        maxHeight: Infinity
    }
}) {
    const [fileErrors, setFileErrors] = React.useState([]);
    const [editImage, setEditImage] = React.useState(null);
    const [isUploading, setIsUploading] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [errorLoading, setErrorLoading] = React.useState(false);
    const { t } = useTranslation();

    const isImage = React.useMemo(() => {
        if (file) {
            const extension = file.substring(file.lastIndexOf('.'));
            return UploadFileZone.imageFileTypes.includes(extension);
        }

        return false;
    }, [file]);

    const errors = {
        'file-invalid-type': t('ticket.event.dropzone_error_file_invalid_type'),
        'file-too-large': t('ticket.event.dropzone_error_file_too_large', { maxSize: Math.trunc(maxSize / 1000000) }),
        'file-too-small': t('ticket.event.dropzone_error_file_too_small'),
        'too-many-files': t('ticket.event.dropzone_error_too_many_files'),
        'image-pixels-too-large': t('ticket.event.dropzone_error_image_pixels_too_large', { maxPixels: Math.sqrt(maxPixels) })
    };

    const _onUpload = React.useCallback(
        file => {
            // for some reason the view calls this extension
            // but performs a split('.') anyway
            const queryParams = { ...getQueryParams(file), extension: file.name };

            // Used to add /events/xxx in the case of charts for example
            const urlParams = { ...getURLParams(file), organizationId: organization.id };

            const uploadModel = new uploadConfigModel();

            setIsUploading(true);
            setErrorLoading(false);
            uploadModel
                .uploadConfig(urlParams, queryParams)
                .then(uploadConfig => {
                    // Upload
                    return uploadConfig.uploadFile(file);
                })
                .then(fileUpload => {
                    return fileUpload.save(urlParams);
                })
                .then(({ response }) => {
                    // all good!
                    onUpload(response);
                })
                .catch(e => {
                    onError(e);
                })
                .finally(() => {
                    setIsUploading(false);
                    setIsLoading(true);
                    //Close modalEditImage
                    setEditImage(null);
                });
        },
        [onUpload, onError, shouldUpload, getQueryParams, getURLParams, setFileErrors, uploadConfigModel, organization]
    );

    const onDrop = React.useCallback(
        async (files, rejected) => {
            let _fileErrors = [];
            if (rejected.length) {
                _fileErrors = rejected[0]['errors'].map(({ code }) => code);
            }
            setFileErrors(_fileErrors);

            const [file] = files;

            if (!shouldUpload(file)) {
                return;
            }

            if (!file) {
                return;
            }

            try {
                // We block the upload of gigantic pictures in prod only.
                // We allow their upload on staging to debug issues there.
                if (maxPixels && window.location.hostname === 'admin.weezevent.com' && (await getImageDimensions(file)) > maxPixels) {
                    setFileErrors(['image-pixels-too-large']);
                    return;
                }
            } catch (error) {
                console.error(error);
                setFileErrors(['file-invalid-type']);
                return;
            }
            const extension = file.path.substring(file.path.lastIndexOf('.'));
            if (UploadFileZone.imageFileTypes.includes(extension)) {
                return setEditImage(file);
            }

            _onUpload(file);
            onSelect(file);
        },
        [_onUpload, shouldUpload, onSelect]
    );

    const onErrorLoading = React.useCallback(() => {
        setIsLoading(false);
        setErrorLoading(true);
    }, []);

    return (
        <React.Fragment>
            <ModalEditImage
                isOpen={Boolean(editImage)}
                imageFile={editImage}
                isSubmitting={isUploading}
                onClose={() => {
                    setEditImage(null);
                    setFileErrors([]);
                }}
                onSubmit={_onUpload}
                onSelectFile={onDrop}
                fileTypes={fileTypes}
                maxSize={maxSize}
                fileErrors={fileErrors}
                {...cropModalProps}
            />
            <div className={`upload-zone-file-container ${className}`}>
                {title && <Form.Label label={title} optionalText={optional ? t('ticket.common.labelOptional') : false} />}
                {subtitle && <Form.Sublabel label={subtitle} />}
                <div className="upload-zone-file-content">
                    {!file ? (
                        <UploadDropZone
                            onFileSelected={onDrop}
                            acceptedFileExtensions={fileTypes}
                            disabled={disabled}
                            maxSize={maxSize}
                            label={labelDropzone}
                            textInfo={subLabelDropzone}
                            isUploading={isUploading}
                        />
                    ) : (
                        <UploadPreview
                            onDrop={onDrop}
                            errorLoading={errorLoading}
                            onLoad={() => setIsLoading(false)}
                            onError={onErrorLoading}
                            onDelete={onDelete}
                            fileSrc={file}
                            fileTypes={fileTypes}
                            maxSize={maxSize}
                            isImage={isImage}
                            isLoading={isLoading}
                            onChangeEditImage={setEditImage}
                        />
                    )}
                    {!customErrors
                        ? fileErrors.map(error => {
                              return (
                                  <div className={'dropzone-error-over-limit'} key={error}>
                                      {errors[error]}
                                  </div>
                              );
                          })
                        : Boolean(fileErrors.length) && customErrors}
                </div>
            </div>
        </React.Fragment>
    );
}

UploadFileZone.propTypes = {
    organization: PropTypes.object.isRequired,
    disabled: PropTypes.bool,
    onUpload: PropTypes.func,
    onError: PropTypes.func,
    onDelete: PropTypes.func,
    getURLParams: PropTypes.func,
    getQueryParams: PropTypes.func,
    shouldUpload: PropTypes.func,
    fileTypes: PropTypes.array,
    uploadConfigModel: PropTypes.object,
    labelDropzone: PropTypes.node,
    subLabelDropzone: PropTypes.node,
    maxSize: PropTypes.number,
    title: PropTypes.node,
    subtitle: PropTypes.node,
    file: PropTypes.string,
    optional: PropTypes.bool,
    className: PropTypes.string,
    cropModalProps: PropTypes.object
};

UploadFileZone.imageFileTypes = ['.jpg', '.jpeg', '.png', '.gif'];
UploadFileZone.defaultFileTypes = ['.jpg', '.jpeg', '.png', '.gif', '.pdf', '.doc'];
